import React, { useEffect, useState } from 'react';
import { formatAmount } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { resolveTaxRegime } from '../../../utils/tax-regime';
import { billTaxSlabs, NOT_GST_REGISTERED } from '../../../utils/bill-tax-slabs';
import { GstBreakupDisplay } from '../GstBreakupDisplay';
import type { PrintState, ReceiptPreviewProps } from './types';

const PAYMENT_LABEL: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  SPLIT: 'Split',
  WALLET: 'Wallet',
  STORE_CREDIT: 'Store credit',
  CREDIT: 'Account credit',
  NETBANKING: 'Net banking',
};

// Width determines font size; 58mm is more compact
const WIDTH_CLASS: Record<string, string> = {
  '80mm': 'w-[72mm]',
  '58mm': 'w-[52mm] text-[10px]',
};

// ─── Receipt body (shared between screen preview and print) ───────────────────

function ReceiptBody({
  bill,
  items,
  tenant,
  isReprint,
  loyaltyPointsEarned,
  currency,
  timeZone,
}: Pick<
  ReceiptPreviewProps,
  'bill' | 'items' | 'tenant' | 'isReprint' | 'loyaltyPointsEarned' | 'timeZone'
> & {
  currency: CurrencyCode;
}) {
  const date = new Date(bill.created_at).toLocaleString('en-IN', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone,
  });

  // Tax vocabulary derives from the tenant currency (regime = f(currency)).
  // INR → GST_INDIA, so the tax-id label stays `GSTIN` (byte-identical).
  const regime = resolveTaxRegime(currency);

  // GST slabs in the tax type the bill was saved with (gst-parked 080): the
  // popup after payment shows the same CGST + SGST or IGST as the saved bill.
  const isInterState = bill.supply_type === 'INTER';
  const slabs = billTaxSlabs(items, bill.supply_type);
  // gst-parked 090 (D5): billed while not GST registered — no GSTIN, no tax.
  const noGst = regime.regime === 'GST_INDIA' && bill.supply_type === 'NONE';

  // Cash rounding is its own line; TOTAL is what was charged.
  const rounding = Number(bill.rounding_adjustment ?? 0);

  return (
    <div className="font-mono text-xs leading-snug">
      {/* 1. Shop header */}
      <div className="text-center mb-2">
        {isReprint && (
          <p className="font-bold text-neutral-500 border border-neutral-400 inline-block px-2 mb-1">
            REPRINT
          </p>
        )}
        <p className="font-bold text-sm">{tenant.name}</p>
        <p className="text-neutral-600">{tenant.address}</p>
        <p className="text-neutral-600">Ph: {tenant.phone}</p>
        {noGst ? (
          <p className="text-neutral-600">{NOT_GST_REGISTERED}</p>
        ) : (
          tenant.gstin && regime.taxIdLabel && (
            <p className="text-neutral-600">
              {regime.taxIdLabel}: {tenant.gstin}
            </p>
          )
        )}
      </div>

      <div className="border-t border-dashed border-neutral-400 my-1" />

      {/* 2. Bill number + date */}
      <div className="mb-1">
        <div className="flex justify-between">
          <span>Bill#</span>
          <span className="font-semibold">{bill.bill_number}</span>
        </div>
        <div className="flex justify-between">
          <span>Date</span>
          <span>{date}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-neutral-400 my-1" />

      {/* 3. Items */}
      <table className="w-full" role="table">
        <thead>
          <tr className="text-left text-neutral-500">
            <th scope="col" className="font-normal pb-0.5 w-1/2">Item</th>
            <th scope="col" className="font-normal pb-0.5 text-right">Qty×Rate</th>
            <th scope="col" className="font-normal pb-0.5 text-right">Amt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="tabular-nums">
              <td className="pr-1 align-top">{item.product_name}</td>
              <td className="text-right align-top whitespace-nowrap">
                {Number.isInteger(Number(item.quantity))
                  ? item.quantity
                  : Number(item.quantity).toFixed(3)}
                ×{formatAmount(item.unit_price, currency)}
              </td>
              <td className="text-right align-top">
                {formatAmount(item.line_total, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-dashed border-neutral-400 my-1" />

      {/* 4. Tax breakdown */}
      {!noGst && bill.tax_total > 0 && slabs.length > 0 && (
        <>
          <GstBreakupDisplay
            slabs={slabs}
            totalTax={bill.tax_total}
            sellerGstin={tenant.gstin}
            isInterState={isInterState}
            variant="compact"
            taxRegime={regime}
            currency={currency}
          />
          <div className="border-t border-dashed border-neutral-400 my-1" />
        </>
      )}

      {/* 5. Grand total */}
      <div className="tabular-nums space-y-0.5">
        {bill.discount_total > 0 && (
          <div className="flex justify-between">
            <span>Discount</span>
            <span>- {formatAmount(bill.discount_total, currency)}</span>
          </div>
        )}
        {rounding !== 0 && (
          <div className="flex justify-between">
            <span>Round Off</span>
            <span>
              {rounding < 0 ? '- ' : '+ '}
              {formatAmount(Math.abs(rounding), currency)}
            </span>
          </div>
        )}
        <div className="flex justify-between font-bold text-sm">
          <span>TOTAL</span>
          <span>{formatAmount(bill.grand_total + rounding, currency)}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-neutral-400 my-1" />

      {/* 6. Payment info */}
      {bill.payment_method && (
        <div className="tabular-nums space-y-0.5">
          {bill.payments && bill.payments.length > 1 ? (
            // An exchange credit or a split: one line per tender, as charged.
            bill.payments.map((p, i) => (
              <div key={i} className="flex justify-between">
                <span>Paid ({PAYMENT_LABEL[p.payment_method] ?? p.payment_method})</span>
                <span>{formatAmount(Number(p.amount), currency)}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between">
              <span>Paid ({PAYMENT_LABEL[bill.payment_method] ?? bill.payment_method})</span>
              <span>{formatAmount(bill.amount_paid, currency)}</span>
            </div>
          )}
          {bill.change_due > 0 && (
            <div className="flex justify-between">
              <span>Change</span>
              <span>{formatAmount(bill.change_due, currency)}</span>
            </div>
          )}
        </div>
      )}

      {/* 7. Customer info */}
      {(bill.customer_name || loyaltyPointsEarned) && (
        <>
          <div className="border-t border-dashed border-neutral-400 my-1" />
          {bill.customer_name && (
            <div className="flex justify-between">
              <span>Customer</span>
              <span>{bill.customer_name}</span>
            </div>
          )}
          {bill.customer_phone && (
            <div className="flex justify-between">
              <span>Phone</span>
              <span>{bill.customer_phone}</span>
            </div>
          )}
          {loyaltyPointsEarned !== undefined && loyaltyPointsEarned > 0 && (
            <div className="flex justify-between text-brand-600">
              <span>Points Earned</span>
              <span>+{loyaltyPointsEarned}</span>
            </div>
          )}
        </>
      )}

      {/* 8. Footer */}
      <div className="border-t border-dashed border-neutral-400 my-1" />
      <p className="text-center text-neutral-500">{tenant.footer || 'Thank you! Visit again.'}</p>
    </div>
  );
}

// ─── ReceiptPreview ───────────────────────────────────────────────────────────

export function ReceiptPreview({
  bill,
  items,
  tenant,
  variant = '80mm',
  onPrint,
  autoPrint = false,
  isReprint = false,
  isOffline = false,
  showPreview = true,
  showPrintControl = true,
  loyaltyPointsEarned,
  currency = 'INR',
  timeZone,
  className,
}: ReceiptPreviewProps) {
  const [printState, setPrintState] = useState<PrintState>('preview');

  useEffect(() => {
    if (autoPrint) {
      handlePrint();
    }
  }, [autoPrint]);

  function handlePrint() {
    setPrintState('printing');
    try {
      onPrint?.();
      setTimeout(() => setPrintState('printed'), 1200);
    } catch {
      setPrintState('error');
    }
  }

  function handleRetry() {
    setPrintState('preview');
  }

  return (
    <div className={['flex flex-col items-center gap-3', className ?? ''].filter(Boolean).join(' ')}>
      {showPreview && (
        <div
          className={[
            'bg-white border border-neutral-200 shadow-md p-3',
            WIDTH_CLASS[variant] ?? WIDTH_CLASS['80mm'],
          ].join(' ')}
          role="document"
          aria-label={`Receipt ${bill.bill_number}`}
        >
          <ReceiptBody
            bill={bill}
            items={items}
            tenant={tenant}
            isReprint={isReprint}
            loyaltyPointsEarned={loyaltyPointsEarned}
            currency={currency}
            timeZone={timeZone}
          />
        </div>
      )}

      {isOffline && (
        <p className="text-xs text-amber-600 font-medium">Offline — printing locally</p>
      )}

      {/* Print controls */}
      {showPrintControl && (
      <div className="flex flex-col items-center gap-2 w-full max-w-[200px]">
        {printState === 'error' ? (
          <div role="alert" className="text-center">
            <p className="text-sm text-error-600 mb-2">Printer not connected</p>
            <button
              type="button"
              onClick={handleRetry}
              className="py-1.5 px-4 rounded border border-error-300 text-error-600 text-sm hover:bg-error-50 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : printState === 'printed' ? (
          <div className="flex items-center gap-1.5 text-success-600 text-sm font-medium">
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 8l3.5 3.5L13 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Receipt Printed
          </div>
        ) : (
          <button
            type="button"
            onClick={handlePrint}
            disabled={printState === 'printing'}
            aria-label="Print receipt"
            className="w-full py-2 px-4 rounded-lg bg-neutral-800 text-white text-sm font-medium hover:bg-neutral-900 disabled:opacity-60 transition-colors"
          >
            {printState === 'printing' ? 'Printing…' : 'Print Receipt'}
          </button>
        )}
      </div>
      )}
    </div>
  );
}
